import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, Users, ShieldCheck, Hourglass, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Stat } from "@/pages/analytics/TrafficPanel";

const FREQ_COLOR = { "Very Active": "text-[#34D399] border-[#34D399]/40", Active: "text-[#22D3EE] border-[#22D3EE]/40", "Low Activity": "text-primary border-primary/40", Inactive: "text-slate-400 border-white/15" };

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "Never");

export function GuideActivityPanel({ data, onChanged }) {
  const [view, setView] = useState("all");
  const [busy, setBusy] = useState(null);
  const c = data.counts;
  const rows = data.guides.filter((g) => view === "all" || (view === "active" ? g.frequency !== "Inactive" : view === "trial" ? g.trial : !g.trial));

  const setAccess = async (g, full) => {
    setBusy(g.user_id);
    try {
      await api.post(`/admin/guides/${g.user_id}/access`, { full });
      toast.success(full ? `${g.name || g.email} now has full access.` : `${g.name || g.email} is back on a fresh 14-day trial.`);
      await onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change that account."); }
    finally { setBusy(null); }
  };

  const copyEmails = () => {
    navigator.clipboard?.writeText(rows.map((g) => g.email).join(", ")).catch(() => {});
    toast.success(`Copied ${rows.length} email${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="space-y-6" data-testid="guide-activity-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Guides" value={data.total} accent="text-slate-100" testid="stat-guides-total" />
        <Stat label="Very active" value={c["Very Active"]} accent="text-[#34D399]" />
        <Stat label="Active" value={c.Active} />
        <Stat label="Low activity" value={c["Low Activity"]} accent="text-primary" />
        <Stat label="Inactive (30d)" value={c.Inactive} accent="text-slate-400" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {[["all", "All"], ["active", "Active only"], ["trial", "Trials"], ["full", "Full accounts"]].map(([id, label]) => (
            <Button key={id} size="sm" data-testid={`guides-view-${id}`} variant={view === id ? "default" : "outline"} onClick={() => setView(id)} className={view === id ? "bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9]" : ""}>{label}</Button>
          ))}
        </div>
        <Button size="sm" variant="outline" data-testid="copy-guide-emails-btn" onClick={copyEmails} className="gap-2"><Copy className="w-4 h-4" /> Copy {rows.length} email{rows.length === 1 ? "" : "s"}</Button>
      </div>

      <div className="hq-glass rounded-2xl border border-white/10 overflow-auto max-h-[70vh]">
        <table className="w-full text-sm" data-testid="guides-table">
          <thead className="sticky top-0 bg-[#0b1220] text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>{["Guide", "Status", "Total logins", "Last login", "30-day logins", "Frequency", "Classes", "Students", "Joined", "Access"].map((h) => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">No Guides in this view.</td></tr>}
            {rows.map((g) => (
              <tr key={g.user_id} data-testid={`guide-row-${g.user_id}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3"><p className="font-medium">{g.name || "Unnamed"}{g.is_owner && <span className="ml-2 text-[10px] text-primary">owner</span>}</p><p className="text-xs text-muted-foreground">{g.email}{g.school ? ` · ${g.school}` : ""}</p></td>
                <td className="px-4 py-3">{g.trial ? <span className={`text-xs px-2 py-0.5 rounded-full border ${g.trial_expired ? "text-rose-400 border-rose-400/40" : "text-primary border-primary/40"}`}>{g.trial_expired ? "Trial ended" : `Trial · ${g.trial_days_left}d`}</span> : <span className="text-xs px-2 py-0.5 rounded-full border text-[#34D399] border-[#34D399]/40">Full</span>}</td>
                <td className="px-4 py-3 font-mono-data">{g.total_logins}</td>
                <td className="px-4 py-3 whitespace-nowrap">{fmt(g.last_login)}</td>
                <td className="px-4 py-3 font-mono-data text-[#22D3EE]">{g.recent_login_count}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${FREQ_COLOR[g.frequency]}`}>{g.frequency}</span></td>
                <td className="px-4 py-3 font-mono-data">{g.classes}</td>
                <td className="px-4 py-3 font-mono-data flex items-center gap-1"><Users className="w-3.5 h-3.5 text-muted-foreground" />{g.students}</td>
                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(g.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  {g.is_owner ? <span className="text-xs text-muted-foreground">—</span> : (
                    <Button size="sm" variant="outline" disabled={busy === g.user_id} onClick={() => setAccess(g, !!g.trial)}
                      data-testid={`guide-access-toggle-${g.user_id}`}
                      className={`gap-1.5 whitespace-nowrap ${g.trial ? "border-[#34D399]/50 text-[#34D399]" : ""}`}>
                      {busy === g.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : g.trial ? <ShieldCheck className="w-3.5 h-3.5" /> : <Hourglass className="w-3.5 h-3.5" />}
                      {g.trial ? "Grant full access" : "Back to trial"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
